/**
 * Packaging OS — Shared layout for /app/* nested screens (Phase 1O-B / 1P-B).
 *
 * Renders the public header + trust banner + sidebar nav (with active
 * highlighting via NavLink) + Outlet for the nested route content.
 */

import type { ReactNode } from "react";
import { NavLink } from "react-router-dom";
import { Footer } from "../components/Footer";
import { PublicHeader } from "../components/PublicHeader";
import { TrustBanner } from "../components/TrustBanner";
import { strings } from "../i18n/strings";
import { useLocale } from "../i18n/useLocale";
import { useLocaleSwitch } from "../i18n/useLocaleSwitch";

interface AppLayoutProps {
  children: ReactNode;
}

export function AppLayout({ children }: AppLayoutProps): JSX.Element {
  const [locale] = useLocale();
  const setLocale = useLocaleSwitch();
  const t = strings(locale);

  return (
    <div className="po-page">
      <PublicHeader
        locale={locale}
        onLocaleChange={setLocale}
        switchLanguageLabel={t.switchLanguageLabel}
        brand={t.appName}
        navLinks={[{ to: "/", label: t.ctaBackToLanding }]}
      />

      <main className="po-page-main po-page-main--app">
        <TrustBanner message={t.trustBannerFull} />

        <div className="po-app-shell">
          <aside className="po-app-sidebar" aria-label="navigation">
            <span className="po-app-sidebar__group-label">
              {t.appShellNavCatalog}
            </span>
            <NavLink
              to="/app/catalog/brands"
              className="po-app-sidebar__item"
            >
              {t.catalogBrandsTitle}
            </NavLink>
            <NavLink
              to="/app/catalog/products"
              className="po-app-sidebar__item"
            >
              {t.catalogProductsTitle}
            </NavLink>
            <NavLink
              to="/app/catalog/packaging-projects"
              className="po-app-sidebar__item"
            >
              {t.catalogProjectsTitle}
            </NavLink>

            <span className="po-app-sidebar__group-label">
              {t.appShellNavWorkflows}
            </span>
            <NavLink
              to="/app/workflows/packaging-audit"
              className="po-app-sidebar__item"
            >
              {t.workflowDryRunTitle}
            </NavLink>
            <NavLink
              to="/app/workflows/product-brief"
              className="po-app-sidebar__item"
            >
              {t.intakeTitle}
            </NavLink>
            <NavLink
              to="/app/workflows/label-copy"
              className="po-app-sidebar__item"
            >
              {t.labelCopyTitle}
            </NavLink>
            <NavLink
              to="/app/workflows/material-structure"
              className="po-app-sidebar__item"
            >
              {t.materialTitle}
            </NavLink>
            <NavLink
              to="/app/workflows/sustainability-risk"
              className="po-app-sidebar__item"
            >
              {t.sustTitle}
            </NavLink>
            <NavLink
              to="/app/workflows/supplier-rfq"
              className="po-app-sidebar__item"
            >
              {t.supplierTitle}
            </NavLink>
            <NavLink
              to="/app/workflows/visual-direction"
              className="po-app-sidebar__item"
            >
              {t.visualTitle}
            </NavLink>
            <NavLink
              to="/app/workflows/packaging-scorecard"
              className="po-app-sidebar__item"
            >
              {t.scorecardTitle}
            </NavLink>
            <NavLink
              to="/app/workflows/launch-kit"
              className="po-app-sidebar__item"
            >
              {t.launchKitTitle}
            </NavLink>
            <NavLink
              to="/app/workflows/post-launch-analysis"
              className="po-app-sidebar__item"
            >
              {t.postLaunchTitle}
            </NavLink>
            <NavLink
              to="/app/workflows/review-checklist"
              className="po-app-sidebar__item"
            >
              {t.reviewChecklistTitle}
            </NavLink>

            <span className="po-app-sidebar__group-label">
              {t.appShellNavSettings}
            </span>
            <NavLink to="/app" end className="po-app-sidebar__item">
              {t.appShellApiStatusHeading}
            </NavLink>
          </aside>

          <div className="po-stack">{children}</div>
        </div>
      </main>

      <Footer copyright={t.copyrightLine} />
    </div>
  );
}
